import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { Image, PriceDisplay } from "@/components/atoms";
import type { ProductCardProps } from "./ProductCard.types";
import { getProductCardConfig } from "./ProductCard.config";
import { useAppSelector } from "@/core/hooks";
import { useCart } from "@/hooks/useCart";
import { useModalRouter } from "@/hooks/useModalRouter";
import { useProductOptionBlocks } from "@/hooks/useProductOptionBlocks";
import {
  selectCartItems,
  selectProductOrVariantQuantityInCart,
} from "@/core/orders/selectors";
import { remainingCartQuantity } from "@/core/orders/quantity-limits";
import { imagorService } from "@/helpers";

/**
 * Product Card Component - Figma Design Implementation
 */
export const ProductCard: React.FC<ProductCardProps> = React.memo(
  ({
    product,
    onClick,
    onAddToCart,
    loading = false,
    size = "medium",
    display = "vertical",
    background: _background,
    imageBackground = "background.default",
    showQuantity = false,
  }) => {
    const theme = useTheme();
    const { addItem } = useCart();
    const { navigateToModal } = useModalRouter();
    const { isBlocking: optionBlocksUnknown, requiresChoice } =
      useProductOptionBlocks(product);

    // Get quantity from cart or use prop (including variants)
    const quantity = useAppSelector((state) =>
      selectProductOrVariantQuantityInCart(state, product)
    );
    const isInCart = quantity > 0;

    // Быстрое добавление минует карточку товара и её собственный гейт
    // потолка (AddToCartButton), поэтому корзину читаем здесь напрямую.
    //
    // Подписка — на selectCartItems (массив позиций), а не на весь слайс
    // orders: на мутации isSyncing/lastSyncError/pendingChanges Immer рождает
    // новую ссылку на currentOrder, но items при этом не трогает
    // (структурное разделение), и selectCartItems в таких случаях отдаёт ТУ
    // ЖЕ ссылку — без этого один клик «+» перерисовывал бы все карточки
    // списка трижды (addPosition → syncOrderStart → syncOrderSuccess), а
    // список не виртуализован.
    const cartItems = useAppSelector(selectCartItems);
    const isCartFull = remainingCartQuantity(cartItems) < 1;

    const productImage = product.images?.[0]?.url || null;
    const productPrice = product.pricePoint || 0;

    // Get configuration for current size
    const config = useMemo(() => getProductCardConfig(size), [size]);

    // Transform image URL with imagor service
    const transformedImageUrl = useMemo(() => {
      if (!productImage) return null;
      try {
        return imagorService.transformUrl(productImage, {
          width: config.imageSize,
          height: config.imageSize,
          smart: true,
        });
      } catch (error) {
        console.error("Error transforming image URL:", error);
        return productImage;
      }
    }, [productImage, config.imageSize]);

    const handleClick = React.useCallback(() => {
      if (!loading) {
        // Open product modal with product ID
        navigateToModal("product", product.id);

        // Call external callback if provided
        if (onClick) {
          onClick(product);
        }
      } else {
        console.log("Click ignored - loading:", { loading });
      }
    }, [navigateToModal, onClick, loading, product]);

    const handleAddToCart = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!loading) {
          // Товар с видимым обязательным блоком опций сервер больше не
          // добавит телом {productId, quantity} — 422 с кодом 13222. Быстрый
          // «+» идёт мимо формы выбора, поэтому вместо добавления открывает
          // карточку товара.
          if (requiresChoice) {
            navigateToModal("product", product.id);
            return;
          }

          // Быстрое добавление минует карточку товара, поэтому свой гейт:
          // без него потолок закрывался бы только на одном из трёх путей.
          if (isCartFull) return;

          const result = addItem(product, 1, []);

          // Call external callback if provided
          if (onAddToCart) {
            onAddToCart(product);
          }

          // Log result for debugging
          if (!result.success) {
            console.error("Failed to add item to cart:", result.error);
          }
        }
      },
      [
        addItem,
        navigateToModal,
        onAddToCart,
        loading,
        product,
        requiresChoice,
        isCartFull,
      ]
    );

    // Unified layout with CSS-based display control
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: `calc(${config.imageSize}px + ${config.padding} + ${config.padding})`,
          height: display === "vertical" ? "100%" : "auto",
          display: "flex",
          flexDirection: display === "horizontal" ? "row" : "column",
          borderRadius: theme.spacing(config.borderRadius),
          p: config.padding,
          gap: config.gap,
        }}
      >
        {/* Image Container */}
        <Box
          onClick={handleClick}
          sx={{
            cursor: "pointer",
            ...(display === "horizontal" && {
              mr: 2, // Отступ справа для изображения в горизонтальном layout
            }),
          }}
        >
          <Image
            src={transformedImageUrl || ""}
            alt={product.name}
            width={config.imageSize}
            height={config.imageSize}
            responsive={true}
            showLoadingState={true}
            borderRadius={theme.spacing(config.imageBorderRadius)}
            sx={{
              width: display === "horizontal" ? config.imageSize : "100%",
              height: display === "horizontal" ? config.imageSize : "auto",
              aspectRatio: "1 / 1",
              opacity: loading ? 0.7 : 1,
              transition: theme.transitions.create("opacity", {
                duration: theme.transitions.duration.short,
              }),
              backgroundColor: imageBackground,
            }}
          />
        </Box>

        {/* Content Container */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent:
              display === "horizontal" ? "space-between" : "flex-start",
            minWidth: 0,
            gap: config.gap,
          }}
        >
          {/* Product Details */}
          <Box
            onClick={handleClick}
            sx={{
              cursor: "pointer",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: config.gap,
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontWeight: "bold",
                fontSize: config.titleFontSize,
                color: theme.palette.text.primary,
              }}
            >
              {product.name}
            </Typography>

            {product.description && (
              <Typography
                fontSize={config.descriptionFontSize}
                color="text.secondary"
                sx={{
                  ...(display === "horizontal" && {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }),
                }}
              >
                {product.description}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              mt: display === "vertical" ? 1 : 0,
            }}
          >
            <Box
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                display:
                  size === "small"
                    ? "flex"
                    : {
                        xs: "flex",
                        lg: "none",
                      },
              }}
            >
              <Typography fontSize={config.priceFontSize} fontWeight={600}>
                <PriceDisplay value={productPrice} />
              </Typography>
              {/* Button */}
              <IconButton
                onClick={handleAddToCart}
                disabled={loading || optionBlocksUnknown || isCartFull}
                sx={{
                  backgroundColor: isInCart ? "primary.main" : "action.focus",
                  borderRadius: theme.spacing(2),
                  color: isInCart ? "primary.contrastText" : "inherit",
                  "&:hover": {
                    backgroundColor: isInCart ? "primary.dark" : "action.hover",
                  },
                }}
              >
                {isInCart ? <CheckIcon /> : <AddIcon />}
              </IconButton>
            </Box>
            <Button
              endIcon={
                isInCart ? (
                  <CheckIcon fontSize="large" />
                ) : (
                  <AddIcon fontSize="large" />
                )
              }
              onClick={handleAddToCart}
              disabled={loading || optionBlocksUnknown || isCartFull}
              sx={{
                backgroundColor: isInCart ? "primary.main" : "background.paper",
                borderRadius: 2,
                px: 2,
                height: 36,
                color: isInCart ? "primary.contrastText" : "text.primary",
                fontWeight: "bold",
                fontSize: config.priceFontSize,
                "&:hover": {
                  backgroundColor: isInCart ? "primary.dark" : "action.hover",
                },
                display:
                  size === "small"
                    ? "none"
                    : {
                        xs: "none",
                        lg: "flex",
                      },
              }}
            >
              {showQuantity && isInCart ? (
                `${quantity} шт`
              ) : (
                <PriceDisplay value={productPrice} />
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  },
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    return (
      prevProps.product.id === nextProps.product.id &&
      prevProps.product.name === nextProps.product.name &&
      prevProps.product.pricePoint === nextProps.product.pricePoint &&
      prevProps.product.description === nextProps.product.description &&
      prevProps.loading === nextProps.loading &&
      prevProps.size === nextProps.size &&
      prevProps.display === nextProps.display &&
      prevProps.background === nextProps.background &&
      prevProps.imageBackground === nextProps.imageBackground &&
      prevProps.showQuantity === nextProps.showQuantity
    );
  }
);

ProductCard.displayName = "ProductCard";
